"use client";

import Link from "next/link";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";

/* ============================================================
   Subcategory hub — shared composer for the 9 services subcategory hubs.
   Accepts typed data, renders the standard 6-section template with
   an additional bespoke "service tree" visualization.
   ============================================================ */

export type AccentName = "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";

export interface SubStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: AccentName;
}

export interface SubChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: AccentName;
}

export interface SubFeature {
  title: string;
  description: string;
  icon: string;
  accent: AccentName;
}

export interface SubUseCase {
  title: string;
  body: string;
  icon: string;
  accent: AccentName;
}

export interface SubWhy {
  title: string;
  body: string;
  metric?: string;
  accent: AccentName;
}

export interface SubFaq {
  q: string;
  a: string;
}

export interface SubcategoryHubData {
  meta: { title: string; description: string; canonical: string };
  parentHref: string;
  parentLabel: string;
  eyebrow: string;
  hero: {
    title: string;
    titleAccent: string;
    lede: string;
    ctaPrimary: { label: string; href: string };
    ctaSecondary: { label: string; href: string };
    accent: AccentName;
  };
  stats: SubStat[];
  children: SubChild[];
  features: SubFeature[];
  useCases: SubUseCase[];
  why: SubWhy[];
  faq: SubFaq[];
  cta: {
    eyebrow: string;
    title: string;
    description: string;
    primary: { label: string; href: string };
    secondary: { label: string; href: string };
    trustItems: string[];
  };
  /* Wrap colors per section, in order: overview, features, useCases, why, faq, cta */
  wraps: {
    overview: AccentName;
    features: AccentName;
    useCases: AccentName;
    why: AccentName;
    faq: AccentName;
    cta: AccentName;
  };
}

function ServiceTree({ children, accent }: { children: SubChild[]; accent: AccentName }) {
  return (
    <div className="relative w-full max-w-[420px] aspect-square mx-auto">
      <svg viewBox="0 0 420 420" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="subCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--color-emerald)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--color-emerald)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="210" cy="210" r="180" fill="url(#subCore)" />
        <circle cx="210" cy="210" r="135" fill="none" stroke="rgb(255 255 255 / 0.05)" strokeDasharray="1 3" />
        <circle cx="210" cy="210" r="85" fill="none" stroke="rgb(255 255 255 / 0.05)" strokeDasharray="2 6" />
        {/* Connection lines */}
        {children.map((c, i) => {
          const angle = (i / children.length) * Math.PI * 2 - Math.PI / 2;
          const ROUND = (n: number) => Math.round(n * 100) / 100;
          const x = ROUND(210 + Math.cos(angle) * 165);
          const y = ROUND(210 + Math.sin(angle) * 165);
          return (
            <line
              key={`l-${c.code}`}
              x1="210"
              y1="210"
              x2={x}
              y2={y}
              stroke={`var(--color-${c.accent})`}
              strokeOpacity="0.3"
              strokeWidth="0.7"
              strokeDasharray="2 4"
            />
          );
        })}
        {/* Child nodes */}
        {children.map((c, i) => {
          const angle = (i / children.length) * Math.PI * 2 - Math.PI / 2;
          const ROUND = (n: number) => Math.round(n * 100) / 100;
          const x = ROUND(210 + Math.cos(angle) * 165);
          const y = ROUND(210 + Math.sin(angle) * 165);
          return (
            <g key={c.code} transform={`translate(${x}, ${y})`}>
              <circle r="26" fill={`color-mix(in oklch, var(--color-${c.accent}) 22%, transparent)`} stroke={`var(--color-${c.accent})`} strokeWidth="0.8" />
              <text textAnchor="middle" y="-2" fontSize="7" fontWeight="800" fill="white">
                {c.code}
              </text>
              <text textAnchor="middle" y="8" fontSize="5.5" fill="rgb(255 255 255 / 0.7)">
                {c.title.split(" ").slice(0, 2).join(" ")}
              </text>
            </g>
          );
        })}
        {/* Center */}
        <g transform="translate(210, 210)">
          <circle r="48" fill="rgb(10 10 18 / 0.92)" />
          <circle r="42" fill="none" stroke={`var(--color-${accent})`} strokeWidth="1" />
          <text x="0" y="-1" textAnchor="middle" fontSize="18" fontWeight="900" fill="white" letterSpacing="-0.5">
            HUB
          </text>
          <text x="0" y="12" textAnchor="middle" fontSize="5.5" fontWeight="700" fill={`var(--color-${accent})`} letterSpacing="1.5">
            {children.length} SERVICES
          </text>
        </g>
      </svg>
    </div>
  );
}

export function SubcategoryHubPage({ data }: { data: SubcategoryHubData }) {
  return (
    <CategoryHubComposer
      overviewWrap={data.wraps.overview}
      featuresWrap={data.wraps.features}
      useCasesWrap={data.wraps.useCases}
      whyWrap={data.wraps.why}
      faqWrap={data.wraps.faq}
      ctaWrap={data.wraps.cta}
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-20">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute top-10 left-1/4 h-72 w-72 rounded-full opacity-20 blur-[130px] animate-pulse-glow"
              style={{ background: `var(--color-${data.hero.accent})` }}
            />
            <div
              className="absolute top-32 right-1/4 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-15 blur-[130px] animate-pulse-glow"
              style={{ animationDelay: "2s" }}
            />
            <div
              className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-12 blur-[140px] animate-pulse-glow"
              style={{ animationDelay: "4s" }}
            />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <Link
                    href={data.parentHref}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold uppercase tracking-[0.2em] hover:scale-[1.02] transition-transform"
                    style={{ color: `var(--color-${data.hero.accent})` }}
                  >
                    <ChevronRight className="h-3 w-3 rotate-180" />
                    {data.parentLabel}
                  </Link>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    {data.hero.title}{" "}
                    <span
                      className="bg-clip-text text-transparent"
                      style={{
                        backgroundImage: `linear-gradient(135deg, var(--color-${data.hero.accent}), var(--color-purple), var(--color-cyan))`,
                      }}
                    >
                      {data.hero.titleAccent}
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    {data.hero.lede}
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href={data.hero.ctaPrimary.href}
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
                      style={{ boxShadow: `0 25px 50px -12px var(--color-${data.hero.accent})` }}
                    >
                      {data.hero.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href={data.hero.ctaSecondary.href}
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      {data.hero.ctaSecondary.label}
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={300}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {data.stats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <ServiceTree children={data.children} accent={data.hero.accent} />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Inside This Hub"
          title="Specialized Services in This Subcategory"
          description="The services below compose this subcategory hub. Each is a coherent operational capability that can be deployed independently or composed with others."
          items={data.children}
          accent={data.wraps.overview}
        />
      }
      features={
        <>
          <Section className="relative">
            <Container>
              <SectionHeading
                eyebrow="Hub Capabilities"
                title="The Features This Subcategory Inherits"
                description="Every service in this hub shares the capabilities below — drawn from the Perception X2 platform."
                accent={data.wraps.features}
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.features.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Common Applications"
          title="Where This Hub Delivers Value"
          description="Patterns of engagement where this subcategory hub consistently delivers measurable outcomes."
          cases={data.useCases}
          accent={data.wraps.useCases}
        />
      }
      why={
        <HubWhy
          eyebrow={`Why ${data.hero.title}`}
          title="Differentiators That Matter"
          items={data.why}
          accent={data.wraps.why}
        />
      }
      faq={
        <HubFAQ
          eyebrow="Frequently Asked Questions"
          title={`${data.hero.title} FAQ`}
          items={data.faq}
          accent={data.wraps.faq}
        />
      }
      cta={
        <HubCTA
          eyebrow={data.cta.eyebrow}
          title={data.cta.title}
          description={data.cta.description}
          primary={data.cta.primary}
          secondary={data.cta.secondary}
          trustItems={data.cta.trustItems}
          accent={data.wraps.cta}
        />
      }
    />
  );
}
